/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// tslint:disable:no-reference
/// <reference path="types/MicrosoftMaps/Microsoft.Maps.All.d.ts" />

import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import classname from 'classnames';
import { reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IStoreInfo } from '@msdyn365-commerce-modules/bopis-utilities';

import { IMapCustomData } from './map-custom.data';
import { IFullOrgUnitAvailability } from '@msdyn365-commerce-modules/retail-actions';

import { IMapCustomProps, IMapCustomResources, IPushpinOptionsData } from './map-custom.props.autogenerated';

/**
 * Header Interface.
 */
export interface IHeader {
    headerProps: INodeProps;
    heading: React.ReactNode;
}
/**
 * MapViewProps Interface.
 */
export interface IMapViewProps extends IMapCustomProps<IMapCustomData> {
    ModuleProps: IModuleProps;
    Header: IHeader;
    MapProps: INodeProps;
    Map: Microsoft.Maps.Map | undefined;
}

enum StoreType {
    VenchiStore,
    Retailer
}

interface PushpinData {
    pushpin: Microsoft.Maps.Pushpin;
    storeType: StoreType;
    index: number;
}

/**
 *
 * Map component.
 * @extends {React.Component<IMapCustomProps<IMapCustomData>>}
 */
@observer
class Map extends React.PureComponent<IMapCustomProps<IMapCustomData>> {
    private map: Microsoft.Maps.Map | undefined;
    private clusterLayer: Microsoft.Maps.ClusterLayer | undefined;
    private pushpinMap: globalThis.Map<string, PushpinData> = new globalThis.Map();
    private selectedPushPin: PushpinData | undefined = undefined;
    private clusterClickHandler: Microsoft.Maps.IHandlerId | undefined = undefined;
    private cachedLastInlineShownStoreInfo: IStoreInfo | undefined;
    private cachedOrgUnitFoundCount: number = 0;
    //TODO finire sviluppo
    // public previousPositionUser = undefined;

    private readonly mapRef: React.RefObject<HTMLElement> = React.createRef<HTMLElement>();

    public constructor(props: IMapCustomProps<IMapCustomData>) {
        super(props);
    }

    public componentDidMount(): void {
        const {
            context: {
                telemetry,
                actionContext: {
                    requestContext: { channel }
                }
            }
        } = this.props;

        if (channel && !channel.BingMapsApiKey) {
            telemetry.error('BingMapsApiKey is missing.');
            return;
        }

        if (channel && !channel.BingMapsEnabled) {
            telemetry.error('Map is disabled from HQ.');
            return;
        }

        if (this.props.data.storeSelectorStateManager.result?.isMapApiLoaded) {
            this._initMap();
            this._updateMapForOrgUnitChanges();
        }

        when(
            () => !!this.props.data.storeSelectorStateManager.result?.setMapModuleLoaded,
            () => {
                this.props.data.storeSelectorStateManager.result?.setMapModuleLoaded(true);
            }
        );

        reaction(
            () => this.props.data.storeSelectorStateManager.result?.loadMapApi,
            () => {
                this.props.data.storeSelectorStateManager.result?.loadMapApi({
                    key: channel?.BingMapsApiKey,
                    lang: this.props.context.actionContext.requestContext.locale,
                    market: this.props.context?.actionContext?.requestContext?.channel?.ChannelCountryRegionISOCode
                });
            }
        );

        reaction(
            () => this.props.data.storeSelectorStateManager.result?.isMapApiLoaded,
            () => {
                this._initMap();
            }
        );

        // reaction(
        //     () => {
        //         return [
        //             this.props.data.storeSelectorStateManager.result?.context?.orgUnitStoreInformation,
        //             this.props.data.storeSelectorStateManager.result?.selectedStoreLocationId,
        //             this.map
        //         ];
        //     },
        //     () => {
        //         this._updateMap();
        //     }
        // );

        reaction(
            () => this.props.data.storeSelectorStateManager.result?.context?.orgUnitStoreInformation,
            () => {
                this._updateMapForOrgUnitChanges();
            }
        );

        reaction(
            () => this.props.data.storeSelectorStateManager.result?.selectedStoreLocationId,
            () => {
                this._updateMapForSelectedStore();
            }
        );
    }

    public render(): JSX.Element | null {
        const {
            config: { heading, className },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        const shouldDisplayMap = storeSelectorStateManager?.listMapViewState.displayMap;

        const viewProps: IMapViewProps = {
            ...this.props,
            ModuleProps: {
                tag: 'div',
                moduleProps: this.props,
                className: classname('ms-map', { show: shouldDisplayMap }, className)
            },
            Header: {
                headerProps: { className: 'ms-map__header' },
                heading: heading?.text && (
                    <Msdyn365.Text
                        className='ms-map__heading'
                        {...heading}
                        tag={heading.tag || 'h2'}
                        text={heading.text}
                        editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                    />
                )
            },
            MapProps: {
                tag: 'div',
                className: 'ms-map__body',
                ref: this.mapRef
            },
            Map: this.map
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Method to be called on heading change.
     * @param event -Content Edit Event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Initialize the map.
     */
    private readonly _initMap = () => {
        const {
            context: {
                actionContext: {
                    requestContext: { channel }
                }
            },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;
        if (storeSelectorStateManager?.isMapApiLoaded && !this.map) {
            this.map = new Microsoft.Maps.Map(this.mapRef.current as HTMLElement, {
                credentials: channel?.BingMapsApiKey,
                pushpinAccessible: true,
                labelOverlay: Microsoft.Maps.LabelOverlay.hidden,
                showMapTypeSelector: false,
                showLocateMeButton: true,
                showTrafficButton: false,
                navigationBarMode: 3 as Microsoft.Maps.NavigationBarMode,
                enableInertia: true,
                customMapStyle: {
                    elements: {
                        countryRegion: { labelVisible: false }
                    },
                    version: '1.0'
                }
            });
            //TODO restore this pls cause i'll cry if not
            // Microsoft.Maps.Events.addHandler(this.map, 'click', async () => {
            //     await storeSelectorStateManager.setSelectedStoreLocationId(undefined);
            // });
        }
    };

    private checkSameLocation(input: IStoreInfo, secondInput: IStoreInfo) {
        return input.Latitude === secondInput.Latitude && input.Longitude === secondInput.Longitude;
    }

    /**
     * Update the map.
     */
    private readonly _updateMapForOrgUnitChanges = () => {
        const pushpinOptions = this.props.config.pushpinOptions;
        const storeSelectorStateManager = this.props.data.storeSelectorStateManager?.result;
        const orgUnitStoreInformation = storeSelectorStateManager?.context?.orgUnitStoreInformation;
        const selectedStoreLocationId = storeSelectorStateManager?.selectedStoreLocationId;
        const positionUser = storeSelectorStateManager?.lastInlineShownStoreInfo;
        var positionUserChanged: boolean = false;
        var firstOrgUnitsRender: boolean = false;
        if (positionUser) {
            if (
                this.cachedLastInlineShownStoreInfo === undefined ||
                !this.checkSameLocation(positionUser, this.cachedLastInlineShownStoreInfo)
            ) {
                this.cachedLastInlineShownStoreInfo = positionUser;
                positionUserChanged = true;
            }
        }
        if (this.map && orgUnitStoreInformation) {
            this.pushpinMap = new globalThis.Map();
            //TODO RESTORE
            var currentLayer: Microsoft.Maps.Layer = this.map?.layers[0];
            if (currentLayer) {
                currentLayer.clear();
            }
            // if (this.clusterClickHandler) {
            //     Microsoft.Maps.Events.removeHandler(this.clusterClickHandler);
            // }
            this.map.layers.clear();
            this.selectedPushPin = undefined;

            const storeLocationList = orgUnitStoreInformation.filter(store => store.OrgUnitAvailability !== undefined);
            if (this.cachedOrgUnitFoundCount === 0 && storeLocationList.length > 0) {
                this.cachedOrgUnitFoundCount = storeLocationList.length;
                firstOrgUnitsRender = true;
            }
            const firstThreeLocations = storeLocationList
                .slice(0, 3)
                .reduce((acc: Microsoft.Maps.Location[], org: IFullOrgUnitAvailability) => {
                    const storeLocation = org.OrgUnitAvailability?.OrgUnitLocation;
                    if (storeLocation && storeLocation.Latitude && storeLocation.Longitude) {
                        acc.push(new Microsoft.Maps.Location(storeLocation.Latitude, storeLocation.Longitude));
                    }
                    return acc;
                }, []);

            for (const [index, unitStoreInfo] of storeLocationList.entries()) {
                const storeLocation = unitStoreInfo.OrgUnitAvailability?.OrgUnitLocation;
                if (storeLocation?.Latitude && storeLocation.Longitude && storeLocation.OrgUnitNumber) {
                    const isSelectedLocation = selectedStoreLocationId === storeLocation.OrgUnitNumber;
                    const storeType =
                        storeLocation.OrgUnitName && storeLocation.OrgUnitName.startsWith('R ')
                            ? StoreType.Retailer
                            : StoreType.VenchiStore;
                    const options = this._getPushpinOptions(
                        isSelectedLocation,
                        index,
                        storeType,
                        pushpinOptions,
                        storeLocation,
                        this.props.resources
                    );

                    const pushpin = new Microsoft.Maps.Pushpin(
                        new Microsoft.Maps.Location(storeLocation.Latitude, storeLocation.Longitude),
                        options
                    );

                    Microsoft.Maps.Events.addHandler(pushpin, 'click', () => {
                        this.handleClickEventOnPushPinStore(storeLocation.OrgUnitNumber);
                    });

                    this.pushpinMap.set(storeLocation.OrgUnitNumber, { pushpin: pushpin, storeType: storeType, index: index });

                    if (isSelectedLocation) {
                        this.selectedPushPin = { pushpin: pushpin, storeType: storeType, index: index };
                    }
                }
            }

            if (positionUserChanged || firstOrgUnitsRender) {
                if (positionUser?.Latitude && positionUser.Longitude) {
                    const currentLocation = new Microsoft.Maps.Location(positionUser.Latitude, positionUser.Longitude);
                    if (firstThreeLocations) {
                        const boundLocations = [currentLocation, ...firstThreeLocations];
                        this.map.setView({
                            bounds: Microsoft.Maps.LocationRect.fromLocations(boundLocations),
                            padding: 100
                        });
                    }
                } else if (this.selectedPushPin) {
                    const pushpinObject = this.selectedPushPin.pushpin;

                    if (this.map.getZoom() >= 13) {
                        this.map.setView({ center: pushpinObject.getLocation() });
                    } else {
                        this.map.setView({ center: pushpinObject.getLocation(), zoom: 14 });
                    }
                } else {
                    this.map.setView({
                        bounds: Microsoft.Maps.LocationRect.fromShapes(
                            Array.from(this.pushpinMap.values()).map(pushpinData => pushpinData.pushpin)
                        )
                    });
                }
            }

            Microsoft.Maps.loadModule('Microsoft.Maps.Clustering', () => {
                // Check if there are any pushpins to cluster
                if (this.map) {
                    if (this.clusterLayer) {
                        this.clusterLayer.clear();
                        if (this.clusterClickHandler) {
                            Microsoft.Maps.Events.removeHandler(this.clusterClickHandler);
                        }
                    }
                    if (this.pushpinMap.size > 0) {
                        // Convert pushpinMap values to an array of pushpins
                        const pushpins = Array.from(this.pushpinMap.values()).map(pushpinData => pushpinData.pushpin);

                        // Create a ClusterLayer with the pushpins
                        this.clusterLayer = new Microsoft.Maps.ClusterLayer(pushpins, {
                            clusteredPinCallback: this.customizeClusteredPin,
                            gridSize: 92,
                            clusteringEnabled: true
                        });
                        // Set options
                        this.map.setOptions({ pushpinAccessible: true });

                        // Add event handler for cluster click
                        this.clusterClickHandler = Microsoft.Maps.Events.addHandler(this.clusterLayer, 'click', this.clusterClicked);

                        // Insert the cluster layer into the map layers
                        //@ts-ignore
                        this.map.layers.insert(this.clusterLayer);
                    }
                }
            });
        }
    };

    private readonly _updateMapForSelectedStore = () => {
        const pushpinOptions = this.props.config.pushpinOptions;
        const storeSelectorStateManager = this.props.data.storeSelectorStateManager?.result;
        const selectedStoreLocationId = storeSelectorStateManager?.selectedStoreLocationId;

        if (this.map) {
            if (this.selectedPushPin) {
                this.selectedPushPin.pushpin.setOptions(
                    this._getPushpinColorAndIcon(false, this.selectedPushPin.storeType, pushpinOptions)
                );
            }
            if (selectedStoreLocationId) {
                // eslint-disable-next-line security/detect-object-injection
                const currentPushpin = this.pushpinMap.get(selectedStoreLocationId);
                if (currentPushpin) {
                    const pushpinObject = currentPushpin.pushpin;
                    pushpinObject.setOptions(this._getPushpinColorAndIcon(true, currentPushpin.storeType, pushpinOptions));
                    this.selectedPushPin = currentPushpin;
                    const mapLayer: Microsoft.Maps.Layer = this.map?.layers[0];
                    if (mapLayer) {
                        const containedPushpins: (Microsoft.Maps.ClusterPushpin | Microsoft.Maps.Pushpin)[] = mapLayer.getPrimitives() as (
                            | Microsoft.Maps.ClusterPushpin
                            | Microsoft.Maps.Pushpin
                        )[];
                        const mapBounds: Microsoft.Maps.LocationRect = this.map.getBounds();
                        if (!containedPushpins.includes(pushpinObject)) {
                            if (mapBounds && mapBounds.contains(pushpinObject.getLocation())) {
                                const currentZoom = this.map.getZoom();
                                const maxZoom = this.map.getZoomRange().max;
                                if (currentZoom < maxZoom) {
                                    const nextZoom = currentZoom <= 13 ? 14 : currentZoom + 1;
                                    this.map.setView({ center: pushpinObject.getLocation(), zoom: nextZoom });
                                }
                            } else {
                                if (this.map.getZoom() >= 13) {
                                    this.map.setView({ center: pushpinObject.getLocation() });
                                } else {
                                    this.map.setView({ center: pushpinObject.getLocation(), zoom: 14 });
                                }
                            }
                        }
                    }
                }
            }
        }
    };

    // private selectFirstWithinDisplayed(){
    //     if (this.map && this.clusterLayer) {
    //         const currentlyDisplayedPushPinsthis = this.clusterLayer.getDisplayedPushpins();
    //         if (currentlyDisplayedPushPinsthis) {
    //             const first =
    //         }
    //     }
    // }

    private customizeClusteredPin = (cluster: Microsoft.Maps.ClusterPushpin) => {
        Microsoft.Maps.Events.addHandler(cluster, 'click', this.clusterClicked);
    };

    private clusterClicked = (e: any) => {
        if (this.map && e.target) {
            //Check to see if the clicked pushpin has any contained pushpins (this would be a cluster).
            if (e.target.containedPushpins) {
                var locs = [];
                for (var i = 0, len = e.target.containedPushpins.length; i < len; i++) {
                    //Get the location of each pushpin.
                    // eslint-disable-next-line security/detect-object-injection
                    locs.push(e.target.containedPushpins[i].getLocation());
                }

                //Create a bounding box for the pushpins.
                var bounds = Microsoft.Maps.LocationRect.fromLocations(locs);

                //Zoom into the bounding box of the cluster.
                //Add a padding to compensate for the pixel area of the pushpins.
                this.map.setView({ bounds: bounds, padding: 100 });
            }
        }
    };

    //TODO RESTORE
    /**
     * Handle click event.
     * @param orgUnitNumber - Organization unit number.
     */
    private readonly handleClickEventOnPushPinStore = (orgUnitNumber: string | undefined): void => {
        const storeSelectorStateManager = this.props.data.storeSelectorStateManager.result;
        if (orgUnitNumber) {
            //setTimeout(() => {
            storeSelectorStateManager?.setSelectedStoreLocationId(orgUnitNumber);
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- Time out.
            //}, 500);
        }
    };

    /**
     * Get the pushpin option.
     * @param isSelectedLocation - Is selected location.
     * @param index - Index.
     * @param pushpinOptions - PushpinOptions.
     * @returns - The pushpin options.
     */
    private readonly _getPushpinColorAndIcon = (
        isSelectedLocation: boolean,
        storeType: StoreType,
        pushpinOptions?: IPushpinOptionsData
    ) => {
        // const text = undefined;

        // const size = pushpinOptions?.size || 1;
        var color: string | undefined = '#8c7760';
        if (storeType === StoreType.Retailer) {
            color = isSelectedLocation ? pushpinOptions?.selectionColor_retailer || '#e87722' : pushpinOptions?.color_retailer || '#004f71';
        } else {
            color = isSelectedLocation ? pushpinOptions?.selectionColor_store || '#e87722' : pushpinOptions?.color_store || '#542D00';
        }
        //const icon = this._getSvgIcon(size, color, text);
        return {
            // Fallback if icon doesn't render properly
            color: color,
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 400 750">
            <defs>
                <filter id="shadow" x="-100%" y="-100%" width="300%" height="300%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="50"></feGaussianBlur>
                    <feOffset dx="0" dy="80" result="offsetblur"></feOffset>
                    <feComponentTransfer>
                        <feFuncA type="linear" slope="0.3"></feFuncA>
                    </feComponentTransfer>
                    <feMerge>
                        <feMergeNode></feMergeNode>
                        <feMergeNode in="SourceGraphic"></feMergeNode>
                    </feMerge>
                </filter>
            </defs>
            <path fill="#ffffff" fill-opacity="0.8" stroke="${color ||
                '#542D00'}" stroke-width="24" transform="scale(1.2) translate(-36 20)" d="M200,551.7c0,0.1,0.2,0.3,0.2,0.3S375.4,283,375.4,194.6c0-130.1-88.8-186.7-175.4-186.9 C113.4,7.9,24.6,64.5,24.6,194.6c0,88.4,175.3,357.4,175.3,357.4S200,551.7,200,551.7z"/>
            <path fill="${color ||
                '#542D00'}" stroke="#542D00" stroke-opacity="0.5" transform="scale(0.8) translate(48 120)" stroke-width="4" filter="url(#shadow)" d="M200,551.7c0,0.1,0.2,0.3,0.2,0.3S375.4,283,375.4,194.6c0-130.1-88.8-186.7-175.4-186.9 C113.4,7.9,24.6,64.5,24.6,194.6c0,88.4,175.3,357.4,175.3,357.4S200,551.7,200,551.7z"></path>
        </svg>`
        };
    };

    /**
     * Get the pushpin option.
     * @param isSelectedLocation - Is selected location.
     * @param index - Index.
     * @param pushpinOptions - PushpinOptions.
     * @returns - The pushpin options.
     */
    private readonly _getPushpinOptions = (
        isSelectedLocation: boolean,
        index: number,
        storeType: StoreType,
        pushpinOptions?: IPushpinOptionsData,
        storeLocation?: OrgUnitLocation,
        mapResources?: IMapCustomResources
    ) => {
        const { color, icon } = this._getPushpinColorAndIcon(isSelectedLocation, storeType, pushpinOptions);
        if (storeLocation) {
            return {
                // Fallback if icon doesn't render properly
                color: color,
                icon,
                //text: storeLocation.OrgUnitNumber!,
                title: `${storeType === StoreType.Retailer ? mapResources?.storeLabelRetailer : mapResources?.storeLabelVenchi} ${index +
                    1}`
            };
        }
        return {
            // Fallback if icon doesn't render properly
            color: color,
            icon,
            title: storeType === StoreType.Retailer ? mapResources?.storeLabelRetailer : mapResources?.storeLabelVenchi
        };
    };
}

export default Map;
